import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { compose, join, map, split } from 'ramda';
import { merge } from 'rxjs';
import { CARD_ICONS } from '../../../common/latest-content/hint-card/hint-card';
import { LocationType, PageDto } from '../../../core/dto/page';
import { IsavValidators } from '../../../utils/forms/validators';

export type PageType = 'internal' | 'external' | 'card';

const capitalize = (str: string): string => str.substring(0, 1).toUpperCase() + str.substring(1);
const prettifyIconName = compose(join(' '), map(capitalize), split('-'));
const LOGO_TYPE_OPTIONS = CARD_ICONS.map((icon, index) => {
    return {
        value: index,
        text: prettifyIconName(icon),
        icon: icon,
    };
});

@UntilDestroy()
@Component({
    selector: 'isav-pages-form',
    templateUrl: './pages-form.html',
    styleUrls: ['./pages-form.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PagesForm implements OnInit, OnChanges {
    @Input() value: Partial<PageDto>;
    @Output() save = new EventEmitter<Partial<PageDto>>();
    @Output() remove = new EventEmitter<void>();
    @Output() publish = new EventEmitter<Partial<PageDto>>();
    @Output() unpublish = new EventEmitter<void>();

    logoTypeOptions = LOGO_TYPE_OPTIONS;

    type = new UntypedFormControl(null);
    form = new UntypedFormGroup({
        title: new UntypedFormControl(null, [IsavValidators.required('Title is required!')]),
        externalLink: new UntypedFormControl(null),
        logoType: new UntypedFormControl(null),
        image: new UntypedFormControl(null),
        attachments: new UntypedFormControl(null),
        body: new UntypedFormControl(null),
        requireAuthentication: new UntypedFormControl(null),
    });

    get formValue() {
        return {
            ...this.form.value,
            externalLink: this.type.value === 'internal' ? null : this.form.value.externalLink,
        };
    }

    get isValid(): boolean {
        switch (this.type.value) {
            case 'card':
            case 'internal':
                return !!this.form.value?.title;
            case 'external':
                return (
                    !!this.form.value?.externalLink?.title && !!this.form.value?.externalLink?.url
                );
            default:
                return false;
        }
    }

    constructor(private cdRef: ChangeDetectorRef) {}

    ngOnInit(): void {
        merge(this.form.valueChanges, this.form.statusChanges, this.type.valueChanges)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.cdRef.markForCheck();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.value && this.value) {
            this.form.reset(this.value);
            this.type.setValue(this.getType());
            if (this.type.value === 'card') {
                this.type.disable();
            } else {
                this.type.enable();
            }
        }
    }

    onSave() {
        this.save.emit(this.formValue);
    }

    onDelete() {
        this.remove.emit();
    }

    onPublish() {
        this.publish.emit(this.formValue);
    }

    onUnpublish() {
        this.unpublish.emit();
    }

    private getType(): PageType {
        if (this.value.locationType === LocationType.LANDING_CARDS) return 'card';
        if (this.value.externalLink === null) return 'internal';
        return 'external';
    }
}
